import { useEffect, useState } from "react"
import { toast } from "react-toastify"
import clsx from "clsx"

import { useTranslation } from "react-i18next"

import { ModalConfirmAction } from "components/modals/ModalConfirmAction"
import { Column, DataTable } from "components/tables/DataTable"

import { gameService } from "services/game/game.service"
import { CategoryRecord } from "services/game/type"

import { CategoryFormModal, CategoryFormState } from "../CategoryFormModal"
import { playerCategoryUrl } from "../helpers/category-slug"
import { SectionOrderEditor } from "../SectionOrderEditor"

interface CategoriesTabProps {
  geo: string
}

const titleEnOf = (c: CategoryRecord) => (c.title && typeof c.title === "object" ? c.title.en ?? c.key : c.key)

export const CategoriesTab = ({ geo }: CategoriesTabProps) => {
  const { t } = useTranslation("translation", { useSuspense: false })

  const [categories, setCategories] = useState<CategoryRecord[]>([])
  const [loading, setLoading] = useState(false)
  const [editState, setEditState] = useState<CategoryFormState>({ kind: "none" })
  const [membersOf, setMembersOf] = useState<CategoryRecord | null>(null)
  const [deleteCandidate, setDeleteCandidate] = useState<CategoryRecord | null>(null)
  const [refreshKey, setRefreshKey] = useState(0)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setLoading(true)
      const data = await gameService.listCategories()
      if (cancelled) return
      setLoading(false)
      if (data) setCategories(data)
    }
    load()
    return () => {
      cancelled = true
    }
  }, [refreshKey])

  const refresh = () => setRefreshKey((k) => k + 1)

  const requestDelete = (record: CategoryRecord) => {
    if (record.source_type === "BUILTIN") {
      toast.error(String(t("gameManagement.categories.builtinDeleteToast", { defaultValue: "BUILTIN categories can't be deleted" })))
      return
    }
    setDeleteCandidate(record)
  }

  const confirmDelete = async (record: CategoryRecord) => {
    const ok = await gameService.deleteCategory(record.key)
    if (ok) {
      toast.success(String(t("gameManagement.categories.deletedToast", { defaultValue: "Category deleted" })))
      refresh()
    }
  }

  // Drill into members editor for a MANUAL category. We render the existing
  // SectionOrderEditor with allowAddRemove since membership is admin-curated.
  if (membersOf) {
    return (
      <SectionOrderEditor
        sectionKey={membersOf.key}
        geo={geo}
        allowAddRemove={membersOf.source_type === "MANUAL"}
        onBack={() => {
          setMembersOf(null)
          refresh()
        }}
      />
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <header className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-semibold text-xl text-[rgba(6,7,21,1)] dark:text-white">
            {t("gameManagement.categories.title", { defaultValue: "Categories" })}
          </h2>
        </div>
        <button
          type="button"
          onClick={() => setEditState({ kind: "create" })}
          className="btn btn-sm bg-[rgba(132,_0,_168,_1)] hover:bg-[rgba(169,_78,_206,_1)] text-white border-transparent"
        >
          {t("gameManagement.categories.newManual", { defaultValue: "+ New manual category" })}
        </button>
      </header>

      {loading ? (
        <p className="py-8 text-center text-lg text-[rgba(6,7,21,1)] dark:text-white">
          {t("gameManagement.common.loading", { defaultValue: "Loading…" })}
        </p>
      ) : categories.length === 0 ? (
        <p className="py-8 text-center text-sm opacity-70 text-[rgba(6,7,21,1)] dark:text-white">
          {t("gameManagement.categories.empty", { defaultValue: "No categories." })}
        </p>
      ) : (
        <DataTable
          rows={categories}
          rowKey={(c) => c.id}
          columns={categoryColumns({
            t,
            onOpenMembers: setMembersOf,
            onEdit: (record) => setEditState({ kind: "edit", record }),
            onDelete: requestDelete
          })}
        />
      )}

      <CategoryFormModal
        state={editState}
        onClose={() => setEditState({ kind: "none" })}
        onSaved={() => {
          setEditState({ kind: "none" })
          refresh()
        }}
      />

      <ModalConfirmAction
        data={deleteCandidate}
        handler={() => setDeleteCandidate(null)}
        text={
          deleteCandidate
            ? t("gameManagement.categories.confirmDelete", {
                defaultValue: `Delete category "{{key}}"? This removes it from the homepage, clears all its game orders, and makes /casino/{{slug}} return 404 for anyone with that link. Cannot be undone.`,
                key: deleteCandidate.key,
                slug: deleteCandidate.slug
              })
            : ""
        }
        actionText={t("common.delete", { defaultValue: "Delete" })}
        actionHandler={confirmDelete}
      />
    </div>
  )
}

type Translate = (key: string, options?: Record<string, unknown>) => string

const categoryColumns = ({
  t,
  onOpenMembers,
  onEdit,
  onDelete
}: {
  t: Translate
  onOpenMembers: (c: CategoryRecord) => void
  onEdit: (c: CategoryRecord) => void
  onDelete: (c: CategoryRecord) => void
}): Column<CategoryRecord>[] => [
  { header: t("gameManagement.categories.col.key", { defaultValue: "Key" }), render: (c) => <span className="font-mono text-xs">{c.key}</span> },
  {
    header: t("gameManagement.categories.col.slug", { defaultValue: "URL" }),
    render: (c) => (
      <a
        href={playerCategoryUrl(c.slug)}
        target="_blank"
        rel="noreferrer"
        className="font-mono text-xs underline decoration-dotted underline-offset-2"
        title={t("gameManagement.categories.openOnSiteTip", { defaultValue: "Open this category on the player site" })}
      >
        /casino/{c.slug}
      </a>
    )
  },
  { header: t("gameManagement.categories.col.titleEn", { defaultValue: "Title (en)" }), render: (c) => titleEnOf(c) },
  {
    header: t("gameManagement.categories.col.type", { defaultValue: "Type" }),
    render: (c) => (
      <span
        className={clsx(
          "px-2 py-0.5 rounded text-xs",
          c.source_type === "BUILTIN"
            ? "bg-black/[0.05] dark:bg-white/[0.05]"
            : c.source_type === "MANUAL"
            ? "bg-[rgba(132,_0,_168,_0.12)] text-[rgba(132,_0,_168,_1)] dark:text-[rgba(169,_78,_206,_1)]"
            : "bg-amber-500/10 text-amber-600 dark:text-amber-400"
        )}
      >
        {c.source_type.toLowerCase()}
      </span>
    )
  },
  {
    header: t("gameManagement.categories.col.onHomepage", { defaultValue: "On homepage" }),
    render: (c) =>
      c.on_homepage ? (
        <span className="text-xs">{t("gameManagement.common.yes", { defaultValue: "yes" })}</span>
      ) : (
        <span
          className="text-xs opacity-60"
          title={t("gameManagement.categories.notOnHomepageTip", {
            defaultValue:
              "The category page exists and is reachable, but no carousel is shown on the lobby. Add it in the Homepage tab."
          })}
        >
          {t("gameManagement.categories.notPlaced", { defaultValue: "not placed" })}
        </span>
      )
  },
  {
    header: t("gameManagement.categories.col.indexed", { defaultValue: "Indexed" }),
    render: (c) => (
      <span className="text-xs">
        {c.seo?.noindex || c.key === "favourite" || c.key === "recent"
          ? t("gameManagement.common.no", { defaultValue: "no" })
          : t("gameManagement.common.yes", { defaultValue: "yes" })}
      </span>
    )
  },
  {
    header: t("gameManagement.categories.col.enabled", { defaultValue: "Enabled" }),
    render: (c) => (
      <span className="text-xs">
        {c.enabled
          ? t("gameManagement.common.yes", { defaultValue: "yes" })
          : t("gameManagement.common.no", { defaultValue: "no" })}
      </span>
    )
  },
  {
    header: t("gameManagement.categories.col.orderable", { defaultValue: "Orderable" }),
    render: (c) => (
      <span className="text-xs">
        {c.is_orderable
          ? t("gameManagement.common.yes", { defaultValue: "yes" })
          : t("gameManagement.common.no", { defaultValue: "no" })}
      </span>
    )
  },
  {
    header: t("gameManagement.categories.col.members", { defaultValue: "Members" }),
    render: (c) =>
      c.source_type === "BUILTIN" ? (
        c.builtin_qualifying_count !== null ? (
          <span
            className="text-xs"
            title={t("gameManagement.categories.builtinFilterCountTip", {
              defaultValue: "Number of games currently matching the BUILTIN filter"
            })}
          >
            {t("gameManagement.categories.filterBased", {
              defaultValue: "filter-based ({{count}})",
              count: c.builtin_qualifying_count
            })}
          </span>
        ) : (
          <span
            className="text-xs opacity-60"
            title={t("gameManagement.categories.computedTip", { defaultValue: "Game order is computed for this section" })}
          >
            —
          </span>
        )
      ) : (
        <span className="text-xs">{c.member_count}</span>
      )
  },
  {
    header: t("gameManagement.categories.col.actions", { defaultValue: "Actions" }),
    align: "right",
    render: (c) => (
      <div className="flex gap-2 justify-end">
        {c.is_orderable && c.source_type !== "RULE" && (
          <button
            type="button"
            onClick={() => onOpenMembers(c)}
            className="btn btn-xs bg-transparent border-[rgba(132,_0,_168,_0.5)] text-[rgba(132,_0,_168,_1)] dark:text-white hover:bg-[rgba(132,_0,_168,_0.08)]"
          >
            {c.source_type === "MANUAL"
              ? t("gameManagement.categories.members", { defaultValue: "Members" })
              : t("gameManagement.categories.order", { defaultValue: "Order" })}
          </button>
        )}
        <button type="button" onClick={() => onEdit(c)} className="btn btn-xs btn-ghost border border-black/10 dark:border-white/10">
          {t("common.edit", { defaultValue: "Edit" })}
        </button>
        <button
          type="button"
          onClick={() => onDelete(c)}
          disabled={c.source_type === "BUILTIN"}
          className="btn btn-xs btn-ghost border border-black/10 dark:border-white/10 disabled:opacity-30"
          title={
            c.source_type === "BUILTIN"
              ? t("gameManagement.categories.builtinDeleteToast", { defaultValue: "BUILTIN categories can't be deleted" })
              : t("common.delete", { defaultValue: "Delete" })
          }
        >
          {t("common.delete", { defaultValue: "Delete" })}
        </button>
      </div>
    )
  }
]
